/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from 'react';
import {
  Collapse,
  Form,
  Input,
  Row,
  Col,
  Divider,
  Button,
  Select,
  Upload,
  Typography,
  Spin,
  Skeleton,
  Radio,
  FormItemProps,
} from 'antd';
import {
  CaretRightOutlined,
  DeleteOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import {
  AUTH_MESSAGE,
  COUNTRY_CURRENCY,
  DELETE_BTN,
  GUTTER,
  LABEL,
  MAIN_BRANCH,
  PHONE_NUMBER_INVALID,
  regex,
  TEXT,
  TITLE,
} from '@moxie/constants';
import {
  checkInstitutionName,
  getInstituteTypes,
  fetchServiceCountry,
  getServices,
  getWorkflow,
  uploadLogo,
} from '@crm/services.api';
import {
  ICRMInstitutionForm,
  IGetPopupContainer,
  IServices,
} from '@shared-components/models';
import { useAppSelector } from '@crm/core';
import { SearchableSelect } from '@shared-components/elements';
import { errorHandler } from '@moxie/utils';
import { normalizePhrase } from '@moxie/shared';
const { Panel } = Collapse;
const { Option } = Select;

const InstitutionForm: React.FC<ICRMInstitutionForm> = ({
  form,
  submitFormData,
  setSubmitDisabled,
  isSubmitting,
  isEdit,
}: ICRMInstitutionForm) => {
  const [servicesData, setServicesData] = useState<IServices[]>([]);
  const [serviceWorkflow, setServiceWorkflow] = useState<any>([]);
  const [instituteType, setInstituteType] = useState<any>([]);
  const [signedURL, setSignedURL] = useState<string>();
  const [logoPath, setLogoPath] = useState<string>();
  const [serviceId, setServiceId] = useState<string>();
  const [workflowCountry, setWorkflowCountry] = useState<any>();
  const [countryList, setCountryList] = useState<any[]>([]);
  const [serviceLoading, setServiceLoading] = useState<boolean>(false);
  const [typeLoading, setTypeLoading] = useState<boolean>(false);
  const [workflowLoading, setWorkflowLoading] = useState<boolean>(false);
  const [nameValidationStatus, setNameValidationStatus] = useState<
    FormItemProps['validateStatus']
  >('');
  const [prevName, setPrevName] = useState<string | undefined>();

  const { institutionData, instituteLoading } = useAppSelector((state) => ({
    institutionData: state.institutions.institution,
    instituteLoading: state.institutions.loading,
  }));

  const handleFile = async (info: any) => {
    const formData = new FormData();
    formData.append(info.filename, info.file);
    try {
      const response = await uploadLogo(formData);
      setSignedURL(response?.data?.data?.signedURL);
      setLogoPath(response?.data?.data?.name);
      setSubmitDisabled(false);
    } catch (err) {
      errorHandler(err);
    }
  };

  const onFieldsChange = (allFields: string | any[]) => {
    if (allFields.length === 1) {
      setSubmitDisabled(false);
    }
  };

  const LOGO_UPLOAD_SETTINGS = {
    showUploadList: false,
    accept: '.jpg, .jpeg, .png',
    maxCount: 1,
    multiple: false,
    customRequest: handleFile,
  };

  const handleFormDataSubmit = (formData: any) => {
    const data = formData;
    data.logo_path = logoPath && logoPath;
    data.email =
      data?.branches?.find((d: any) => d.is_main_branch)?.email || '';
    if (institutionData?.id) {
      submitFormData({ ...data, id: institutionData.id });
    } else {
      submitFormData(data);
    }
  };

  const handleServiceChange = async (id: string) => {
    setServiceId(id);
    form.setFieldsValue({
      workflows: undefined,
    });
  };

  const fetchCountries = async (id: string) => {
    const countries = await fetchServiceCountry(id);
    setCountryList(countries?.data?.data);
  };

  const handleCountryChange = async (name: any) => {
    setWorkflowCountry(name);
    form.setFieldsValue({
      workflows: undefined,
    });
  };

  const getAllInstituteTypes = async () => {
    setTypeLoading(true);
    const instituteTypes = await getInstituteTypes();
    setInstituteType(instituteTypes.data.data);
    setTypeLoading(false);
  };

  const fetchServices = async () => {
    setServiceLoading(true);
    const data = await getServices();
    if (data?.data?.success) {
      setServicesData(data?.data?.data);
      setServiceLoading(false);
    } else {
      errorHandler(AUTH_MESSAGE.FETCH_SERVICE_FAIL);
    }
  };
  const getWorkflows = async () => {
    setWorkflowLoading(true);
    if (serviceId && workflowCountry) {
      const response = await getWorkflow(serviceId, workflowCountry);
      setServiceWorkflow(response?.data?.data);
    }
    setWorkflowLoading(false);
  };

  const handleNameChange = async (rule: any, value: any): Promise<any> => {
    if (!value) {
      setNameValidationStatus('error');
      return Promise.resolve(true);
    }

    if (value?.length > 100) {
      setNameValidationStatus('error');
      return Promise.resolve(true);
    }

    if (value?.length === 0) {
      setNameValidationStatus('error');
      return Promise.resolve(true);
    } else {
      setNameValidationStatus('validating');

      if (value !== '' || null || undefined) {
        const formatValue = value?.trim();
        if (isEdit && formatValue?.toLowerCase() === prevName?.trim()) {
          return;
        }
        const result = await validateName({
          name: normalizePhrase(formatValue)!,
          id: institutionData?.id,
        });

        if (result) {
          setNameValidationStatus('success');
          return Promise.resolve(true);
        } else {
          setNameValidationStatus('error');
          throw new Error(AUTH_MESSAGE.NAME_EXIST);
        }
      }
    }
  };

  const validateName = async (value: { name: string; id?: number }) => {
    try {
      const res = await checkInstitutionName(value?.name, value?.id);
      return !!res?.data?.success;
    } catch (err) {
      return false;
    }
  };

  const addNewBranch = async (add: any) => {
    add();
  };

  const isMainBranch = (name: number) => {
    const { branches } = form.getFieldsValue();
    if (Array.isArray(branches) && !(branches.length === 0)) {
      if (branches[name]?.is_main_branch) {
        return true;
      } else {
        return false;
      }
    }
    return;
  };

  const handleBranchChange = (name: number) => {
    const { branches } = form.getFieldsValue();
    branches.forEach((branch: any, i: any) => {
      if (i !== name) {
        Object.assign(branch, { is_main_branch: false });
      } else {
        Object.assign(branch, { is_main_branch: true });
      }
    });
    setTimeout(() => {
      form.setFieldsValue({ branches });
    }, 0);
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (isEdit) {
        setPrevName(form.getFieldValue('name')?.toLowerCase());
      }
    }, 0);

    return () => {
      clearTimeout(timeout);
    };
  }, [instituteLoading]);

  useEffect(() => {
    getWorkflows();
    if (serviceId) {
      fetchCountries(serviceId);
    }
  }, [serviceId, workflowCountry]);

  useEffect(() => {
    servicesData.length === 0 && fetchServices();
    if (institutionData && isEdit) {
      setServiceId(institutionData?.service_id);
      setWorkflowCountry(institutionData?.country);
      setServiceWorkflow(() =>
        institutionData?.workflows?.map((item: any) => {
          return item;
        })
      );
      form.setFieldsValue({
        ...institutionData,
        workflows: institutionData?.workflows?.map((item: any) => item?.id),
      });
    } else {
      form.setFieldsValue({});
    }
  }, [institutionData]);

  useEffect(() => {
    fetchServices();
    getAllInstituteTypes();
  }, []);

  return instituteLoading ? (
    <Skeleton active />
  ) : (
    <>
      <Spin spinning={serviceLoading || isSubmitting}>
        <Form
          layout="vertical"
          className="institution-add-form"
          form={form}
          onFinish={handleFormDataSubmit}
          scrollToFirstError={true}
          onFieldsChange={onFieldsChange}
        >
          <Collapse
            defaultActiveKey={['1']}
            bordered={false}
            ghost={true}
            expandIcon={({ isActive }) => (
              <CaretRightOutlined rotate={isActive ? 90 : 0} />
            )}
          >
            <Panel header={<span>{TEXT.PRIMARY_INFORMATION}</span>} key="1">
              <Row gutter={GUTTER}>
                <Col
                  span={7}
                  className="text-align-center institution-add-form__avatar"
                >
                  <Upload
                    {...LOGO_UPLOAD_SETTINGS}
                    listType="picture-card"
                    name="logo_path"
                    showUploadList={false}
                    className="institution-add-form__upload-image-height"
                  >
                    {institutionData?.logo_path || signedURL ? (
                      <img
                        className="institution-add-form__image"
                        src={signedURL || institutionData?.logo_path}
                        alt="Logo"
                      />
                    ) : (
                      <div>
                        <PlusOutlined />
                        <p className="ant-upload-hint">
                          <Typography.Text type="secondary">
                            {TITLE.UPLOAD_LOGO}
                          </Typography.Text>
                        </p>
                      </div>
                    )}
                  </Upload>
                </Col>
                <Col span={17}>
                  <Row>
                    <Col span={24}>
                      <Form.Item
                        data-testid="crm-insitution-name"
                        name="name"
                        validateStatus={nameValidationStatus}
                        rules={[
                          {
                            required: true,
                            max: 100,
                          },
                          {
                            validator: handleNameChange,
                          },
                        ]}
                        label={LABEL.NAME}
                      >
                        <Input className="note-title-input" />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={GUTTER}>
                    <Col span={12}>
                      <div id="institution_service" className="relative">
                        <Form.Item
                          data-testid="crm-institution-service"
                          name="service_id"
                          rules={[{ required: true }]}
                          label={LABEL.SERVICE}
                        >
                          <Select
                            onChange={handleServiceChange}
                            getPopupContainer={(): IGetPopupContainer =>
                              document.getElementById('institution_service')
                            }
                          >
                            {serviceLoading ? (
                              <Option value={''} disabled>
                                <Spin
                                  spinning
                                  size="small"
                                  className="full-width"
                                />
                              </Option>
                            ) : (
                              servicesData?.map((item, i: number) => (
                                <Option key={i} value={item?.id}>
                                  {item.name}
                                </Option>
                              ))
                            )}
                          </Select>
                        </Form.Item>
                      </div>
                    </Col>
                    <Col span={12}>
                      <div id="institution_country" className="relative">
                        <Form.Item
                          data-testid="crm-institution-country"
                          name="country"
                          rules={[{ required: true }]}
                          label={LABEL.COUNTRY}
                        >
                          <SearchableSelect
                            onChange={(name) => handleCountryChange(name)}
                            getPopupContainer={(): IGetPopupContainer =>
                              document.getElementById('institution_country')
                            }
                          >
                            {countryList?.map((item, i: number) => (
                              <Option key={i} value={item.name}>
                                {item.name}
                              </Option>
                            ))}
                          </SearchableSelect>
                        </Form.Item>
                      </div>
                    </Col>
                  </Row>
                  <Row gutter={GUTTER}>
                    <Col span={12}>
                      <div id="institution_workflow" className="relative">
                        <Form.Item
                          data-testid="crm-institution-workflow"
                          name="workflows"
                          rules={[{ required: true }]}
                          label={LABEL.WORKFLOW}
                        >
                          <Select
                            showArrow={true}
                            mode="multiple"
                            getPopupContainer={(): IGetPopupContainer =>
                              document.getElementById('institution_workflow')
                            }
                          >
                            {workflowLoading ? (
                              <Option value={''} disabled>
                                <Spin
                                  spinning
                                  size="small"
                                  className="full-width"
                                />
                              </Option>
                            ) : (
                              serviceWorkflow?.map((item: any, i: number) => {
                                return (
                                  <Option key={i} value={item?.id || null}>
                                    {item?.workflowType?.name}
                                  </Option>
                                );
                              })
                            )}
                          </Select>
                        </Form.Item>
                      </div>
                    </Col>
                    <Col span={12}>
                      <div id="institution_type" className="relative">
                        <Form.Item
                          data-testid="crm-institution-type"
                          name="institution_type_id"
                          rules={[{ required: true }]}
                          label={LABEL.TYPE}
                        >
                          <Select
                            getPopupContainer={(): IGetPopupContainer =>
                              document.getElementById('institution_type')
                            }
                          >
                            {typeLoading ? (
                              <Option value={''} disabled>
                                <Spin
                                  spinning
                                  size="small"
                                  className="full-width"
                                />
                              </Option>
                            ) : (
                              instituteType?.map((item: any, i: number) => (
                                <Option key={i} value={item.id}>
                                  {item.name}
                                </Option>
                              ))
                            )}
                          </Select>
                        </Form.Item>
                      </div>
                    </Col>
                  </Row>
                  <Row gutter={GUTTER}>
                    <Col span={12}>
                      <div id="institution_currency" className="relative">
                        <Form.Item
                          data-testid="crm-institution-currency"
                          name="currency"
                          rules={[{ required: true }]}
                          label={LABEL.CURRENCY}
                        >
                          <Select
                            getPopupContainer={(): IGetPopupContainer =>
                              document.getElementById('institution_currency')
                            }
                          >
                            {COUNTRY_CURRENCY.map((item, i: number) => (
                              <Option key={i} value={item.name}>
                                {item.name}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </div>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        data-testid="crm-institution-code"
                        name="identification_system_code"
                        rules={[{ max: 15 }]}
                        label={TEXT.INSTITUTION_CODE}
                      >
                        <Input className="note-title-input" />
                      </Form.Item>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Panel>
          </Collapse>
          <Divider />
          <Collapse
            defaultActiveKey={['1']}
            bordered={false}
            ghost={true}
            expandIcon={({ isActive }) => (
              <CaretRightOutlined rotate={isActive ? 90 : 0} />
            )}
          >
            <Panel header={<span>{TEXT.BRANCHES}</span>} key="1">
              <Form.List
                initialValue={[{ is_main_branch: true }]}
                name="branches"
              >
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name, fieldKey, ...restField }) => (
                      <div key={key}>
                        <Row gutter={24}>
                          <Col span={12}>
                            <Form.Item
                              data-testid="crm-institution-branch-name"
                              {...restField}
                              label={LABEL.NAME}
                              name={[name, 'name']}
                              rules={[
                                {
                                  required: true,
                                  max: 50,
                                },
                              ]}
                            >
                              <Input />
                            </Form.Item>
                          </Col>
                          <Col span={8}>
                            <Form.Item
                              name={[name, 'is_main_branch']}
                              className="institution-add-form__checkbox"
                            >
                              <div>
                                <Radio
                                  id={`${key}`}
                                  className="radio_dyno"
                                  name="main_branch_input"
                                  checked={isMainBranch(name)}
                                  onChange={() => handleBranchChange(name)}
                                />
                                <label
                                  className="margin-left-1"
                                  htmlFor={`${key}`}
                                >
                                  {MAIN_BRANCH}
                                </label>
                              </div>
                            </Form.Item>
                          </Col>
                          {!isMainBranch(name) ? (
                            <Col span={4}>
                              <Button
                                onClick={() => remove(name)}
                                type="link"
                                danger
                                icon={<DeleteOutlined />}
                              >
                                {DELETE_BTN}
                              </Button>
                            </Col>
                          ) : null}
                        </Row>
                        <Collapse
                          defaultActiveKey={['1']}
                          bordered={false}
                          ghost={true}
                          expandIcon={({ isActive }) => (
                            <CaretRightOutlined rotate={isActive ? 90 : 0} />
                          )}
                        >
                          <Panel header={<span>{TEXT.ADDRESS}</span>} key="1">
                            <Form.Item>
                              <Input.Group>
                                <Row gutter={GUTTER}>
                                  <Col span={12}>
                                    <Form.Item
                                      data-testid="crm-institution-branch-city"
                                      {...restField}
                                      label={LABEL.CITY}
                                      name={[name, 'address', 'city']}
                                      fieldKey={[fieldKey, 'city']}
                                      rules={[
                                        {
                                          required: true,
                                        },
                                      ]}
                                    >
                                      <Input />
                                    </Form.Item>
                                  </Col>
                                  <Col span={12}>
                                    <Form.Item
                                      data-testid="crm-institution-branch-state"
                                      {...restField}
                                      label={LABEL.STATE}
                                      name={[name, 'address', 'state']}
                                      fieldKey={[fieldKey, 'state']}
                                    >
                                      <Input />
                                    </Form.Item>
                                  </Col>
                                </Row>
                                <Row gutter={GUTTER}>
                                  <Col span={12}>
                                    <Form.Item
                                      data-testid="crm-institution-branch-postalcode"
                                      {...restField}
                                      label={LABEL.POSTAL_CODE}
                                      name={[name, 'address', 'postal_code']}
                                      fieldKey={[fieldKey, 'postal_code']}
                                    >
                                      <Input type={'number'} />
                                    </Form.Item>
                                  </Col>
                                  <Col span={12}>
                                    <Form.Item
                                      data-testid="crm-institution-branch-street"
                                      {...restField}
                                      label={LABEL.STREET}
                                      name={[name, 'address', 'street']}
                                      fieldKey={[fieldKey, 'street']}
                                    >
                                      <Input />
                                    </Form.Item>
                                  </Col>
                                </Row>
                              </Input.Group>
                            </Form.Item>
                          </Panel>
                        </Collapse>
                        <Collapse
                          defaultActiveKey={['1']}
                          bordered={false}
                          ghost={true}
                          expandIcon={({ isActive }) => (
                            <CaretRightOutlined rotate={isActive ? 90 : 0} />
                          )}
                        >
                          <Panel
                            header={<span>{TEXT.CONTACT_DETAILS}</span>}
                            key="1"
                          >
                            <Row gutter={GUTTER}>
                              <Col span={12}>
                                <Form.Item
                                  data-testid="crm-institution-branch-number"
                                  {...restField}
                                  label={LABEL.PHONE_NUMBER}
                                  name={[name, 'phone_number']}
                                  normalize={(value) => value.replace(/ /g, '')}
                                  fieldKey={[fieldKey, 'phone_number']}
                                  rules={[
                                    {
                                      pattern: new RegExp(regex.ONLY_NUMBER),
                                      message: PHONE_NUMBER_INVALID,
                                    },
                                  ]}
                                >
                                  <Input />
                                </Form.Item>
                              </Col>
                              <Col span={12}>
                                <Form.Item
                                  data-testid="crm-institution-branch-email"
                                  {...restField}
                                  label={LABEL.EMAIL}
                                  name={[name, 'email']}
                                  fieldKey={[fieldKey, 'email']}
                                  normalize={(value) => value.replace(/ /g, '')}
                                  rules={[
                                    {
                                      pattern: new RegExp(regex.EMAIL),
                                      message: AUTH_MESSAGE.INVALID_EMAIL,
                                    },
                                  ]}
                                >
                                  <Input />
                                </Form.Item>
                              </Col>
                            </Row>
                            <Row gutter={GUTTER}>
                              <Col span={12}>
                                <Form.Item
                                  data-testid="crm-institution-branch-fax"
                                  {...restField}
                                  label={LABEL.FAX}
                                  name={[name, 'fax']}
                                  fieldKey={[fieldKey, 'fax']}
                                >
                                  <Input />
                                </Form.Item>
                              </Col>
                              <Col span={12}>
                                <Form.Item
                                  data-testid="crm-institution-branch-website"
                                  {...restField}
                                  label={LABEL.WEBSITE}
                                  name={[name, 'website']}
                                  fieldKey={[fieldKey, 'website']}
                                  rules={[
                                    {
                                      required: false,
                                      pattern: new RegExp(regex.WEBSITE),
                                      message: AUTH_MESSAGE.INVALID_WEB_URL,
                                    },
                                  ]}
                                >
                                  <Input />
                                </Form.Item>
                              </Col>
                            </Row>
                          </Panel>
                        </Collapse>
                        <Divider className="institution-add-form__divider" />
                      </div>
                    ))}
                    <Row justify="end">
                      <Form.Item>
                        <Button
                          type="link"
                          onClick={() =>
                            addNewBranch(() => {
                              add();
                            })
                          }
                          icon={<PlusOutlined />}
                        >
                          {TEXT.ADD_ANOTHER_BRANCH}
                        </Button>
                      </Form.Item>
                    </Row>
                  </>
                )}
              </Form.List>
            </Panel>
          </Collapse>
        </Form>
      </Spin>
    </>
  );
};

export default InstitutionForm;
